import React, {Component} from 'react';
import {connect} from 'react-redux';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import MenuItem from '@material-ui/core/MenuItem';
import CircularProgress from '@material-ui/core/CircularProgress';
import {Delete as DeleteIcon, Add as AddIcon} from '@material-ui/icons';
import ImpactDialog from './ImpactDialog';
import Auth from '../../../auth/auth';
import * as A from '../../data-lineage/store/lineageActions';

const auth = new Auth();
const NODE_TYPES = ['application', 'service', 'api', 'file', 'other'];
const EMPTY_FORM = {nodeType: 'application', name: '', description: ''};

class NodeRefsPanel extends Component {
  state = {
    editing: null,
    form: EMPTY_FORM,
    saving: false,
    filterType: 'application',
    usage: {},
    deleting: null,
    usageLoading: false,
    renameId: null,
    renameDraft: '',
  };

  componentDidMount() {
    this.fetchMissingUsage();
  }

  componentDidUpdate(prev) {
    if (prev.nodeRefs !== this.props.nodeRefs) {
      this.fetchMissingUsage();
    }
  }

  fetchMissingUsage() {
    const {nodeRefs, fetchNodeRefUsage} = this.props;
    nodeRefs.forEach((r) => {
      if (r.isActive === 0) return;
      if (this.state.usage[r.id] === undefined) {
        this.setState((s) => ({usage: {...s.usage, [r.id]: 'loading'}}));
        fetchNodeRefUsage(r.id).then((result) => {
          this.setState((s) => ({usage: {...s.usage, [r.id]: result}}));
        }).catch(() => {
          this.setState((s) => ({usage: {...s.usage, [r.id]: null}}));
        });
      }
    });
  }

  startNew = () => this.setState({editing: 'new', form: {...EMPTY_FORM, nodeType: this.state.filterType}});
  cancelEdit = () => this.setState({editing: null, form: EMPTY_FORM});

  save = async () => {
    const {editing, form} = this.state;
    if (!form.name.trim()) return;
    this.setState({saving: true});
    if (editing === 'new') {
      const result = await this.props.createNodeRef(form);
      if (result && result.response) {
        this.setState((s) => ({usage: {...s.usage, [result.response.id]: {nodeCount: 0, diagramCount: 0, diagrams: []}}}));
      }
    } else {
      await this.props.updateNodeRef(editing, form);
    }
    this.setState({saving: false, editing: null, form: EMPTY_FORM});
  };

  startRename = (r) => this.setState({renameId: r.id, renameDraft: r.name});
  cancelRename = () => this.setState({renameId: null, renameDraft: ''});

  commitRename = async (r) => {
    const {renameDraft} = this.state;
    if (!renameDraft.trim() || renameDraft.trim() === r.name) {
      this.cancelRename();
      return;
    }
    await this.props.updateNodeRef(r.id, {
      nodeType: r.nodeType,
      name: renameDraft.trim(),
      description: r.description || '',
    });
    this.cancelRename();
  };

  openDelete = async (r) => {
    this.setState({deleting: r, usageLoading: true});
    try {
      const result = await this.props.fetchNodeRefUsage(r.id);
      this.setState((s) => ({usage: {...s.usage, [r.id]: result}, usageLoading: false}));
    } catch (e) {
      this.setState({usageLoading: false});
    }
  };

  confirmDelete = async () => {
    const {deleting} = this.state;
    if (!deleting) return;
    await this.props.deleteNodeRef(deleting.id);
    this.setState((s) => {
      const usage = {...s.usage};
      delete usage[deleting.id];
      return {deleting: null, usageLoading: false, usage};
    });
  };

  closeDelete = () => this.setState({deleting: null, usageLoading: false});

  render() {
    const {nodeRefs} = this.props;
    const {editing, form, saving, filterType, usage, deleting, usageLoading, renameId, renameDraft} = this.state;
    const isSuperUser = auth.getIsSuperUser();

    const visible = nodeRefs.filter((r) => r.isActive !== 0 && r.nodeType === filterType);

    const addBtn = (
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={this.startNew}
        disabled={!isSuperUser || !!editing}
        data-testid="lc-noderef-add-btn"
      >
        Add
      </Button>
    );

    return (
      <div className="lc-panel" data-testid="lc-noderef-panel">
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8}}>
          <Typography variant="subtitle2">Node References</Typography>
          {isSuperUser ? addBtn : (
            <Tooltip title="Requires super user">{addBtn}</Tooltip>
          )}
        </div>

        <TextField
          select
          label="Node type"
          fullWidth
          margin="dense"
          value={filterType}
          onChange={(e) => this.setState({filterType: e.target.value, editing: null, form: EMPTY_FORM})}
          inputProps={{'data-testid': 'lc-noderef-type-filter'}}
        >
          {NODE_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>

        {visible.length === 0 && (
          <Typography variant="body2" color="textSecondary" style={{marginTop: 8}}>
            No references for this type yet.
          </Typography>
        )}

        {visible.map((r) => {
          const refUsage = usage[r.id];
          const isRenaming = renameId === r.id;

          const deleteBtn = (
            <IconButton
              size="small"
              onClick={() => this.openDelete(r)}
              disabled={!isSuperUser}
              data-testid={`lc-noderef-delete-${r.id}`}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          );

          return (
            <div
              key={r.id}
              className="lc-panel-row"
              data-testid={`lc-noderef-row-${r.id}`}
            >
              {isRenaming ? (
                <TextField
                  size="small"
                  value={renameDraft}
                  autoFocus
                  style={{flex: 1}}
                  onChange={(e) => this.setState({renameDraft: e.target.value})}
                  onBlur={() => this.commitRename(r)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') this.commitRename(r);
                    else if (e.key === 'Escape') this.cancelRename();
                  }}
                  inputProps={{'data-testid': `lc-noderef-rename-${r.id}`}}
                />
              ) : (
                <Typography
                  style={{flex: 1, cursor: isSuperUser ? 'pointer' : 'default'}}
                  title={isSuperUser ? 'Click to rename' : r.name}
                  onClick={() => isSuperUser && this.startRename(r)}
                  data-testid={`lc-noderef-rename-${r.id}`}
                >
                  {r.name}
                </Typography>
              )}

              <Typography
                variant="caption"
                color="textSecondary"
                style={{whiteSpace: 'nowrap'}}
                data-testid={`lc-noderef-usage-${r.id}`}
              >
                {refUsage === 'loading' ? <CircularProgress size={10} /> :
                  refUsage ? `${refUsage.diagramCount} diagram(s)` : ''}
              </Typography>

              {isSuperUser ? deleteBtn : (
                <Tooltip title="Requires super user">{deleteBtn}</Tooltip>
              )}
            </div>
          );
        })}

        {editing === 'new' && (
          <div className="lc-panel-form">
            <Typography variant="subtitle2" gutterBottom>New reference</Typography>
            <TextField
              select label="Node type" fullWidth margin="dense"
              value={form.nodeType}
              onChange={(e) => this.setState({form: {...form, nodeType: e.target.value}})}
              inputProps={{'data-testid': 'lc-noderef-form-type'}}
            >
              {NODE_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField
              label="Name" fullWidth margin="dense" value={form.name}
              onChange={(e) => this.setState({form: {...form, name: e.target.value}})}
              inputProps={{'data-testid': 'lc-noderef-name-input'}}
            />
            <TextField
              label="Description" fullWidth margin="dense" value={form.description}
              onChange={(e) => this.setState({form: {...form, description: e.target.value}})}
              inputProps={{'data-testid': 'lc-noderef-description-input'}}
            />
            <div style={{marginTop: 10, display: 'flex', gap: 8}}>
              <Button
                size="small" variant="contained" color="primary"
                onClick={this.save}
                disabled={saving || !form.name.trim()}
                data-testid="lc-noderef-save-btn"
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button size="small" onClick={this.cancelEdit} data-testid="lc-noderef-cancel-btn">Cancel</Button>
            </div>
          </div>
        )}

        <ImpactDialog
          open={!!deleting}
          onClose={this.closeDelete}
          onConfirm={this.confirmDelete}
          loading={usageLoading}
          usage={deleting ? usage[deleting.id] : null}
          itemName={deleting ? deleting.name : ''}
          itemType="node reference"
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  nodeRefs: state.lineageReducer.nodeRefs || [],
});

export default connect(mapStateToProps, {
  createNodeRef: A.createNodeRef,
  updateNodeRef: A.updateNodeRef,
  deleteNodeRef: A.deleteNodeRef,
  fetchNodeRefUsage: A.fetchNodeRefUsage,
})(NodeRefsPanel);
